import React, { useState, useMemo, useEffect, useRef } from "react";
import "./Salarybreakupcalculator.css";

const DEFAULT_ASSUMPTIONS = {
  basicPctOfGross: 0.4,
  hraPctOfBasic: 0.4,
  pfPctOfBasic: 0.0531,
  gratuityPctOfBasic: 0.0483,
  monthlyPerfPctOfBasic: 0.25,
  quarterlyPerfPctOfBasic: 0.125,
  ltaPctOfBasic: 0.1701,
  medicalPctOfBasic: 0.085,
  conveyancePctOfBasic: 0.0544,
  foodVouchersPctOfBasic: 0,
  onPostPctOfBasic: 0.2721,
};

// Stable empty-object reference so passing "no revision data yet" never
// creates a brand-new object identity on every parent re-render.
const EMPTY_SALARY_OBJ = Object.freeze({});

/**
 * Forward calculation:
 * Given Monthly Basic, calculate all salary components.
 */
function computeBreakup(basicMonthly, a) {
  const gross = a.basicPctOfGross > 0 ? basicMonthly / a.basicPctOfGross : 0;

  const hra = basicMonthly * a.hraPctOfBasic;
  const lta = basicMonthly * a.ltaPctOfBasic;
  const medical = basicMonthly * a.medicalPctOfBasic;
  const conveyance = basicMonthly * a.conveyancePctOfBasic;
  const food = basicMonthly * a.foodVouchersPctOfBasic;

  const special = gross - basicMonthly - hra - lta - medical - conveyance - food;

  const pf = basicMonthly * a.pfPctOfBasic;
  const gratuity = basicMonthly * a.gratuityPctOfBasic;
  const onPost = basicMonthly * a.onPostPctOfBasic;
  const monthlyPerf = basicMonthly * a.monthlyPerfPctOfBasic;
  const quarterlyPerf = basicMonthly * a.quarterlyPerfPctOfBasic;

  const ctc = gross + pf + gratuity + onPost + monthlyPerf + quarterlyPerf;

  return {
    basic: basicMonthly,
    hra,
    special,
    lta,
    medical,
    conveyance,
    food,
    gross,
    pf,
    gratuity,
    onPost,
    monthlyPerf,
    quarterlyPerf,
    ctc,
  };
}

/**
 * Reverse calculation:
 * Given Annual CTC, calculate Monthly Basic.
 */
function basicFromCtc(ctcMonthly, a) {
  const denom =
    (a.basicPctOfGross > 0 ? 1 / a.basicPctOfGross : 0) +
    a.pfPctOfBasic +
    a.gratuityPctOfBasic +
    a.onPostPctOfBasic +
    a.monthlyPerfPctOfBasic +
    a.quarterlyPerfPctOfBasic;

  if (denom <= 0) return 0;

  return ctcMonthly / denom;
}

const fmt = (n) => (isFinite(n) ? Math.round(n).toLocaleString("en-IN") : "-");

export default function SalaryBreakupCalculator({
  onChange,
  employeeName,
  designation,
  salaryAmt,
  disabled = false,
}) {
  const [mode, setMode] = useState(salaryAmt?.inputMode === "ctc" ? "ctc" : "basic");
  const [basicInput, setBasicInput] = useState(salaryAmt?.monthly?.basicSalaryAmt ?? 0);
  const [ctcInput, setCtcInput] = useState(salaryAmt?.annual?.ctcAmt ?? 0);
  const [assumptions, setAssumptions] = useState(DEFAULT_ASSUMPTIONS);
  const [showAssumptions, setShowAssumptions] = useState(false);
   // eslint-disable-next-line
  const [employee, setEmployee] = useState({
    name: "",
    empId: "",
    department: "",
    designation: "",
    location: "",
    level: "",
  });

  useEffect(() => {
    setEmployee((prev) => ({
      ...prev,
      name: employeeName || "",
      designation: designation || "",
    }));
  }, [employeeName, designation]);
  const lastEmittedRef = useRef(null); // last payload *we* sent up via onChange
  const skipNextSyncRef = useRef(false);

  useEffect(() => {
    if (!salaryAmt) return;
    if (
      skipNextSyncRef.current &&
      lastEmittedRef.current &&
      JSON.stringify(salaryAmt) === JSON.stringify(lastEmittedRef.current)
    ) {
      skipNextSyncRef.current = false;
      return;
    }

    const nextMode = salaryAmt?.inputMode === "ctc" ? "ctc" : "basic";
    const nextBasic = salaryAmt?.monthly?.basicSalaryAmt ?? 0;
    const nextCtc = salaryAmt?.annual?.ctcAmt ?? 0;

    setMode((prev) => (prev === nextMode ? prev : nextMode));
    setBasicInput((prev) => (Number(prev) === Number(nextBasic) ? prev : nextBasic));
    setCtcInput((prev) => (Number(prev) === Number(nextCtc) ? prev : nextCtc));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salaryAmt]);
  const basicMonthly = useMemo(() => {
    if (mode === "basic") {
      return Number(basicInput) || 0;
    }
    const ctcMonthly = (Number(ctcInput) || 0) / 12;
    return Math.max(basicFromCtc(ctcMonthly, assumptions), 0);
  }, [mode, basicInput, ctcInput, assumptions]);
  const data = useMemo(() => computeBreakup(basicMonthly, assumptions), [basicMonthly, assumptions]);

  const updateAssumption = (key, value) => {
    setAssumptions((prev) => ({
      ...prev,
      [key]: Number(value),
    }));
  };

  const negativeSpecial = data.special < 0;
  const calculatedSalaryAmt = useMemo(() => {
    const r = (n) => (isFinite(n) ? Math.round(n) : 0);

    return {
      inputMode: mode,
      monthly: {
        basicSalaryAmt: r(data.basic),
        hraAmt: r(data.hra),
        specialAllowanceAmt: r(data.special),
        ltaAmt: r(data.lta),
        medicalAllowanceAmt: r(data.medical),
        conveyanceAllowanceAmt: r(data.conveyance),
        foodVouchersAmt: r(data.food),
        grossSalaryAmt: r(data.gross),
        providentFundAmt: r(data.pf),
        gratuityAmt: r(data.gratuity),
        onPostAllowanceAmt: r(data.onPost),
        monthlyPerformancePayAmt: r(data.monthlyPerf),
        quarterlyPerformancePayAmt: r(data.quarterlyPerf),
        ctcAmt: r(data.ctc),
      },
      annual: {
        basicSalaryAmt: r(data.basic * 12),
        hraAmt: r(data.hra * 12),
        specialAllowanceAmt: r(data.special * 12),
        ltaAmt: r(data.lta * 12),
        medicalAllowanceAmt: r(data.medical * 12),
        conveyanceAllowanceAmt: r(data.conveyance * 12),
        foodVouchersAmt: r(data.food * 12),
        grossSalaryAmt: r(data.gross * 12),
        providentFundAmt: r(data.pf * 12),
        gratuityAmt: r(data.gratuity * 12),
        onPostAllowanceAmt: r(data.onPost * 12),
        monthlyPerformancePayAmt: r(data.monthlyPerf * 12),
        quarterlyPerformancePayAmt: r(data.quarterlyPerf * 12),
        ctcAmt: r(data.ctc * 12),
      },
    };
  }, [data, mode]);
  useEffect(() => {
    if (typeof onChange !== "function" || disabled) return;

    const serialized = JSON.stringify(calculatedSalaryAmt);
    if (serialized === JSON.stringify(lastEmittedRef.current)) {
      return; // nothing actually changed, don't emit / don't loop
    }

    lastEmittedRef.current = calculatedSalaryAmt;
    skipNextSyncRef.current = true;
    onChange(calculatedSalaryAmt);
  }, [calculatedSalaryAmt, disabled, onChange]);
  return (
    <div className="sbc-wrapper">
      {/* HEADER */}
      <div className="sbc-header">
        <h2>Salary Breakup Calculator</h2>
        <p className="sbc-subtitle">
          Enter Monthly Basic or Annual CTC — every other row updates automatically.
        </p>
      </div>

      {/* EMPLOYEE */}
      <div className="sbc-employee-grid">
        <label>Employee Name</label>
        <p className="employee-value">{employeeName || ""}</p>

        <label>Designation</label>
        <p className="employee-value">{designation || ""}</p>
      </div>

      {/* MODE */}
      <div className="sbc-mode-toggle">
        <label className={mode === "basic" ? "active" : ""}>
          <input
            type="radio"
            name="sbc-mode"
            checked={mode === "basic"}
            onChange={() => setMode("basic")}
            disabled={disabled}
          />
          Enter Monthly Basic
        </label>

        <label className={mode === "ctc" ? "active" : ""}>
          <input
            type="radio"
            name="sbc-mode"
            checked={mode === "ctc"}
            onChange={() => setMode("ctc")}
            disabled={disabled}
          />
          Enter Annual CTC
        </label>
      </div>

      {/* PRIMARY INPUT */}
      <div className="sbc-primary-input">
        {mode === "basic" ? (
          <div className="sbc-field">
            <label>Monthly Basic Salary (₹)</label>
            <input
              type="number"
              value={basicInput}
              onChange={(e) => setBasicInput(e.target.value)}
              disabled={disabled}
            />
          </div>
        ) : (
          <div className="sbc-field">
            <label>Annual CTC (₹)</label>
            <input
              type="number"
              value={ctcInput}
              onChange={(e) => setCtcInput(e.target.value)}
              disabled={disabled}
            />
          </div>
        )}
      </div>

      {/* SALARY TABLE */}
      <table className="sbc-table">
        <thead>
          <tr>
            <th>Salary Components</th>
            <th>Monthly</th>
            <th>Annual</th>
            <th>Remarks</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Basic Salary</td>
            <td>{fmt(data.basic)}</td>
            <td>{fmt(data.basic * 12)}</td>
            <td>{Math.round(assumptions.basicPctOfGross * 100)}% of the Gross</td>
          </tr>

          <tr>
            <td>HRA</td>
            <td>{fmt(data.hra)}</td>
            <td>{fmt(data.hra * 12)}</td>
            <td>{Math.round(assumptions.hraPctOfBasic * 100)}% of Basic</td>
          </tr>

          <tr className={negativeSpecial ? "sbc-warning-row" : ""}>
            <td>Special Allowance</td>
            <td>{fmt(data.special)}</td>
            <td>{fmt(data.special * 12)}</td>
            <td>Balance Amount</td>
          </tr>

          <tr className="sbc-section">
            <td colSpan={4}>Flexible Benefit Plan</td>
          </tr>

          <tr>
            <td>LTA</td>
            <td>{fmt(data.lta)}</td>
            <td>{fmt(data.lta * 12)}</td>
            <td>{(assumptions.ltaPctOfBasic * 100).toFixed(1)}% of Basic</td>
          </tr>

          <tr>
            <td>Medical Allowance</td>
            <td>{fmt(data.medical)}</td>
            <td>{fmt(data.medical * 12)}</td>
            <td>{(assumptions.medicalPctOfBasic * 100).toFixed(1)}% of Basic</td>
          </tr>

          <tr>
            <td>Conveyance Allowance</td>
            <td>{fmt(data.conveyance)}</td>
            <td>{fmt(data.conveyance * 12)}</td>
            <td>{(assumptions.conveyancePctOfBasic * 100).toFixed(1)}% of Basic</td>
          </tr>

          <tr>
            <td>Food Vouchers</td>
            <td>{fmt(data.food)}</td>
            <td>{fmt(data.food * 12)}</td>
            <td>{(assumptions.foodVouchersPctOfBasic * 100).toFixed(1)}% of Basic</td>
          </tr>

          <tr className="sbc-subtotal">
            <td>Gross Salary</td>
            <td>{fmt(data.gross)}</td>
            <td>{fmt(data.gross * 12)}</td>
            <td></td>
          </tr>

          <tr>
            <td>Provident Fund</td>
            <td>{fmt(data.pf)}</td>
            <td>{fmt(data.pf * 12)}</td>
            <td>Company Contribution</td>
          </tr>

          <tr>
            <td>Gratuity</td>
            <td>{fmt(data.gratuity)}</td>
            <td>{fmt(data.gratuity * 12)}</td>
            <td>Company Contribution</td>
          </tr>

          <tr>
            <td>On-Post Allowance</td>
            <td>{fmt(data.onPost)}</td>
            <td>{fmt(data.onPost * 12)}</td>
            <td>{(assumptions.onPostPctOfBasic * 100).toFixed(1)}% of Basic</td>
          </tr>

          <tr>
            <td>Monthly Performance Pay</td>
            <td>{fmt(data.monthlyPerf)}</td>
            <td>{fmt(data.monthlyPerf * 12)}</td>
            <td>Payable monthly per Incentive Policy</td>
          </tr>

          <tr>
            <td>Quarterly Performance Pay</td>
            <td>{fmt(data.quarterlyPerf)}</td>
            <td>{fmt(data.quarterlyPerf * 12)}</td>
            <td>Paid in Jul / Oct / Jan / Apr</td>
          </tr>

          <tr className="sbc-total">
            <td>CTC</td>
            <td>{fmt(data.ctc)}</td>
            <td>{fmt(data.ctc * 12)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>

      {negativeSpecial && (
        <p className="sbc-warning-text">
          ⚠ Special Allowance is negative — the fixed components (LTA, Medical, Conveyance, Food
          Vouchers) exceed the Gross for this Basic. Reduce them or increase Basic / CTC.
        </p>
      )}

      <button
        type="button"
        className="sbc-toggle-assumptions"
        onClick={() => setShowAssumptions(!showAssumptions)}
        disabled={disabled}
      >
        {showAssumptions ? "Hide" : "Customize"} calculation rules
      </button>

      {showAssumptions && (
        <div className="sbc-assumptions-panel">
          <div className="sbc-assumption-row">
            <label>Basic as % of Gross</label>
            <input
              type="number"
              step="1"
              value={assumptions.basicPctOfGross * 100}
              onChange={(e) => updateAssumption("basicPctOfGross", e.target.value / 100)}
              disabled={disabled}
            />
          </div>

          <div className="sbc-assumption-row">
            <label>HRA as % of Basic</label>
            <input
              type="number"
              step="1"
              value={assumptions.hraPctOfBasic * 100}
              onChange={(e) => updateAssumption("hraPctOfBasic", e.target.value / 100)}
              disabled={disabled}
            />
          </div>

          <div className="sbc-assumption-row">
            <label>PF as % of Basic</label>
            <input
              type="number"
              step="0.1"
              value={assumptions.pfPctOfBasic * 100}
              onChange={(e) => updateAssumption("pfPctOfBasic", e.target.value / 100)}
              disabled={disabled}
            />
          </div>

          <div className="sbc-assumption-row">
            <label>Gratuity as % of Basic</label>
            <input
              type="number"
              step="0.01"
              value={assumptions.gratuityPctOfBasic * 100}
              onChange={(e) => updateAssumption("gratuityPctOfBasic", e.target.value / 100)}
              disabled={disabled}
            />
          </div>

          <div className="sbc-assumption-row">
            <label>Monthly Performance Pay as % of Basic</label>
            <input
              type="number"
              step="1"
              value={assumptions.monthlyPerfPctOfBasic * 100}
              onChange={(e) => updateAssumption("monthlyPerfPctOfBasic", e.target.value / 100)}
              disabled={disabled}
            />
          </div>

          <div className="sbc-assumption-row">
            <label>Quarterly Performance Pay as % of Basic</label>
            <input
              type="number"
              step="0.1"
              value={assumptions.quarterlyPerfPctOfBasic * 100}
              onChange={(e) => updateAssumption("quarterlyPerfPctOfBasic", e.target.value / 100)}
              disabled={disabled}
            />
          </div>

          <div className="sbc-assumption-row">
            <label>LTA as % of Basic</label>
            <input
              type="number"
              step="0.1"
              value={assumptions.ltaPctOfBasic * 100}
              onChange={(e) => updateAssumption("ltaPctOfBasic", e.target.value / 100)}
              disabled={disabled}
            />
          </div>

          <div className="sbc-assumption-row">
            <label>Medical Allowance as % of Basic</label>
            <input
              type="number"
              step="0.1"
              value={assumptions.medicalPctOfBasic * 100}
              onChange={(e) => updateAssumption("medicalPctOfBasic", e.target.value / 100)}
              disabled={disabled}
            />
          </div>

          <div className="sbc-assumption-row">
            <label>Conveyance Allowance as % of Basic</label>
            <input
              type="number"
              step="0.1"
              value={assumptions.conveyancePctOfBasic * 100}
              onChange={(e) => updateAssumption("conveyancePctOfBasic", e.target.value / 100)}
              disabled={disabled}
            />
          </div>

          <div className="sbc-assumption-row">
            <label>Food Vouchers as % of Basic</label>
            <input
              type="number"
              step="0.1"
              value={assumptions.foodVouchersPctOfBasic * 100}
              onChange={(e) => updateAssumption("foodVouchersPctOfBasic", e.target.value / 100)}
              disabled={disabled}
            />
          </div>

          <div className="sbc-assumption-row">
            <label>On-Post Allowance as % of Basic</label>
            <input
              type="number"
              step="0.1"
              value={assumptions.onPostPctOfBasic * 100}
              onChange={(e) => updateAssumption("onPostPctOfBasic", e.target.value / 100)}
              disabled={disabled}
            />
          </div>
        </div>
      )}
    </div>
  );
}
export { EMPTY_SALARY_OBJ };